// File extension to language mapping
const extensionMap = {
  // JavaScript/TypeScript
  js: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  tsx: 'tsx',
  mjs: 'javascript',
  cjs: 'javascript',

  // Web
  html: 'html',
  htm: 'html',
  css: 'css',
  scss: 'scss',
  sass: 'sass',
  less: 'less',

  // Data
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  xml: 'xml',
  toml: 'toml',

  // Python
  py: 'python',
  pyw: 'python',
  pyi: 'python',

  // Ruby
  rb: 'ruby',
  erb: 'erb',

  // Go
  go: 'go',

  // Rust
  rs: 'rust',

  // Java/Kotlin
  java: 'java',
  kt: 'kotlin',
  kts: 'kotlin',

  // C/C++
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  cc: 'cpp',
  cxx: 'cpp',
  hpp: 'cpp',

  // C#
  cs: 'csharp',

  // PHP
  php: 'php',

  // Swift
  swift: 'swift',

  // Shell
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  fish: 'bash',

  // SQL
  sql: 'sql',

  // Markdown
  md: 'markdown',
  mdx: 'markdown',

  // Config
  env: 'bash',
  gitignore: 'bash',
  dockerfile: 'dockerfile',
};

// Patterns to detect language from code content
const contentPatterns = [
  // JavaScript/TypeScript
  { pattern: /^import\s+.*\s+from\s+['"]|^export\s+(default\s+)?(function|class|const|let|var)/m, lang: 'javascript' },
  { pattern: /^import\s+React|^import\s+\{.*\}\s+from\s+['"]react['"]|<[A-Z][a-zA-Z]*[\s/>]/m, lang: 'jsx' },
  { pattern: /:\s*(string|number|boolean|any)\b|interface\s+\w+\s*\{|type\s+\w+\s*=/m, lang: 'typescript' },
  { pattern: /<[A-Z][a-zA-Z]*.*\/>.*:\s*(string|number|boolean)/m, lang: 'tsx' },

  // Python
  { pattern: /^(from\s+\S+\s+)?import\s+\S+|^def\s+\w+\s*\(|^class\s+\w+.*:/m, lang: 'python' },
  { pattern: /^\s*@\w+(\.\w+)*\s*(\(.*\))?\s*$/m, lang: 'python' },

  // Ruby
  { pattern: /^require\s+['"]|^class\s+\w+\s*(<\s*\w+)?$|^def\s+\w+|^end$/m, lang: 'ruby' },

  // Go
  { pattern: /^package\s+\w+|^func\s+(\(\w+\s+\*?\w+\)\s+)?\w+\s*\(/m, lang: 'go' },

  // Rust
  { pattern: /^(pub\s+)?fn\s+\w+|^(pub\s+)?struct\s+\w+|^use\s+\w+::|^impl\s+/m, lang: 'rust' },

  // Java
  { pattern: /^(public|private|protected)\s+(static\s+)?(class|interface|enum)\s+\w+|^package\s+[\w.]+;/m, lang: 'java' },

  // C/C++
  { pattern: /^#include\s+[<"]/m, lang: 'cpp' },

  // HTML
  { pattern: /^<!DOCTYPE\s+html>|^<html[\s>]|^<head[\s>]|^<body[\s>]/im, lang: 'html' },

  // CSS
  { pattern: /^[.#]?\w+\s*\{[^}]*\}|^@media\s+/m, lang: 'css' },

  // JSON
  { pattern: /^\s*\{[\s\S]*"[\w-]+":\s*["\d\[\{]/m, lang: 'json' },

  // YAML
  { pattern: /^\s*[\w-]+:\s*[^\n]+$|^---\s*$/m, lang: 'yaml' },

  // SQL
  { pattern: /^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s+/im, lang: 'sql' },

  // Shell/Bash
  { pattern: /^#!/m, lang: 'bash' },
  { pattern: /^\$\s+\w+|^(npm|yarn|pnpm|npx|git|cd|ls|mkdir|rm|cp|mv)\s+/m, lang: 'bash' },

  // Dockerfile
  { pattern: /^FROM\s+\w+|^RUN\s+|^COPY\s+|^CMD\s+/m, lang: 'dockerfile' },
];

/**
 * Detect language from file extension
 */
export function detectFromExtension(filename) {
  if (!filename) return null;

  const ext = filename.split('.').pop()?.toLowerCase();
  return extensionMap[ext] || null;
}

/**
 * Detect language from code content
 */
export function detectFromContent(code) {
  if (!code || code.length < 10) return null;

  for (const { pattern, lang } of contentPatterns) {
    if (pattern.test(code)) {
      return lang;
    }
  }

  return null;
}

/**
 * Detect language with fallback chain:
 * 1. Explicit language (from markdown fence)
 * 2. File extension (if filename provided)
 * 3. Content analysis
 * 4. Default to 'text'
 */
export function detectLanguage(code, explicitLang = '', filename = '') {
  // Use explicit language if provided
  if (explicitLang && explicitLang !== 'text') {
    return explicitLang;
  }

  // Try file extension
  const fromExt = detectFromExtension(filename);
  if (fromExt) {
    return fromExt;
  }

  // Try content detection
  const fromContent = detectFromContent(code);
  if (fromContent) {
    return fromContent;
  }

  return 'text';
}

export default detectLanguage;
